import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ENSGridResizeType, INSGridConfig } from '@norfolk-southern/accessns-components';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridOptions, RowNode } from 'ag-grid-community';
import SweetAlert2 from 'sweetalert2';

import { NSMessages } from '@ruby/configs/messages.constants';
import { NSTrackMoveConstants } from '@ruby/configs/track-move.constants';
import { ITrackDrop, ITrackInventory, ITrackMovePayload } from '@ruby/modules/track-move/models/track-move.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { ETrackGridTitle } from '@ruby/shared/enums/track-move.enums';
import { ITrack } from '@ruby/shared/models/inventory-management/inventory-header.interface';

@Component({
  selector: 'ng-track-move',
  templateUrl: './track-move.component.html',
  styleUrls: ['./track-move.component.scss']
})
export class TrackMoveComponent implements OnChanges {
  @Input() fromTrackData!: Array<ITrackInventory>;
  @Input() toTrackData!: Array<ITrackInventory>;
  @Input() fromTrackEquipments!: Array<string>;
  @Input() toTrackEquipments!: Array<string>;
  @Input() inventoryTrackData!: Array<ITrack>;
  @Input() selectedFromTrack?: ITrack;
  @Input() selectedToTrack?: ITrack;
  @Input() loading!: boolean;
  @Input() loadingToTrack!: boolean;
  @Input() performTrackMoves?: boolean;
  @Output() moveTrack = new EventEmitter<ITrackMovePayload>();
  @Output() toTrackSelected = new EventEmitter<ITrack | undefined>();
  selectedTracks = new Map<string, RowNode>();
  resizeType = ENSGridResizeType.SIZECOLUMNSTOFIT;
  inventoryGridTitle = ETrackGridTitle.TRACK_NOT_SELECTED;
  fromTrackConfig: INSGridConfig = NSTrackMoveConstants.fromTrackConfig;
  inventoryTrackConfig: INSGridConfig = NSTrackMoveConstants.inventoryTrackConfig;
  toTrackConfig: INSGridConfig = NSTrackMoveConstants.toTrackConfig;
  fromTrackOptions: GridOptions = NSTrackMoveConstants.fromTrackGridOptions;
  inventoryTrackOptions: GridOptions = NSTrackMoveConstants.inventoryGridOptions;
  toTrackOptions: GridOptions = NSTrackMoveConstants.toTrackGridOptions;
  fromTrackGrid!: AgGridAngular;
  inventoryTrackGrid!: AgGridAngular;
  toTrackGrid!: AgGridAngular;

  constructor() {}

  get isGridReady(): boolean {
    return !!(this.fromTrackGrid && this.fromTrackGrid.api &&
      this.toTrackGrid && this.toTrackGrid.api &&
      this.inventoryTrackGrid && this.inventoryTrackGrid.api);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fromTrackData) {
      if (changes.fromTrackData.currentValue.length === 0 && this.selectedFromTrack && this.fromTrackGrid) {
        this.fromTrackGrid.gridOptions.overlayNoRowsTemplate = NSMessages.trackMoveEmptyRecords;
      } else if (!this.selectedFromTrack && this.fromTrackGrid) {
        this.fromTrackGrid.gridOptions.overlayNoRowsTemplate = NSMessages.trackMoveEmptyTrack;
      }
    }

    if (changes.toTrackData) {
      if (changes.toTrackData.currentValue.length === 0 && this.selectedToTrack && this.toTrackGrid) {
        this.toTrackGrid.gridOptions.overlayNoRowsTemplate = NSMessages.trackMoveEmptyRecords;
      } else if (!this.selectedToTrack && this.toTrackGrid) {
        this.toTrackGrid.gridOptions.overlayNoRowsTemplate = NSMessages.trackMoveEmptyTrack;
      }
    }

    if ((changes.inventoryTrackData || changes.inventoryTrackGrid) && this.selectedToTrack) {
      this.inventoryGridTitle = this.selectedToTrack.name as ETrackGridTitle;
    }
  }

  /**
   * Set From Track Grid
   *
   * @summary Sets the from track grid reference
   * @param $param: AgGridAngular
   * @return void
   */
  setFromTrackGrid($param: AgGridAngular): void {
    this.fromTrackGrid = $param;
    this.fromTrackGrid.api.addEventListener('rowDragEnd', this.fromTrackRowDragEnd.bind(this));
    this.fromTrackGrid.api.addEventListener('rowSelected', this.fromTrackSelection.bind(this));
    this.fromTrackGrid.api.addEventListener('rowDataChanged', this.setFromTrackEquipments.bind(this));
    this.addGridDropZone();
  }

  /**
   * Set Inventory Track Grid
   *
   * @summary Sets the inventory track grid reference
   * @param $param: AgGridAngular
   * @return void
   */
  setInventoryTrackGrid($param: AgGridAngular): void {
    this.inventoryTrackGrid = $param;
    this.inventoryTrackGrid.api.addEventListener('rowDragEnd', this.inventoryTrackRowDragEnd.bind(this));
    this.inventoryTrackGrid.api.addEventListener('rowDragMove', this.inventoryRowDragMove.bind(this));
    this.inventoryTrackGrid.api.addEventListener('rowDragLeave', this.resetInventoryStyle.bind(this));
    this.inventoryTrackGrid.api.addEventListener('selectionChanged', this.setInventoryGridTitle.bind(this));
    this.inventoryTrackGrid.api.addEventListener('rowDataChanged', this.updateInventoryGridSelection.bind(this));
    this.addGridDropZone();
  }

  /**
   * Inventory Row Drag Move
   *
   * @summary Sets the inventory grid data with hover effect
   * @param drop: ITrackDrop
   * @return void
   */
  inventoryRowDragMove(drop: ITrackDrop): void {
    const itemsToUpdate: Array<RowNode> = [];
    this.inventoryTrackGrid.api.forEachNode((node: RowNode) => {
      if (node.data.id === (drop.overNode?.data.id)) {
        node.data = { ...node.data, hover: true };
        itemsToUpdate.push(node.data);
      } else {
        node.data = { ...node.data, hover: false };
        itemsToUpdate.push(node.data);
      }
    });
    drop.api.applyTransaction({ update: itemsToUpdate });
  }

  /**
   * Reset Inventory Style
   *
   * @summary Resets inventory grid row style
   * @return void
   */
  resetInventoryStyle(): void {
    const itemsToUpdate: Array<RowNode> = [];
    this.inventoryTrackGrid.api.forEachNode((node: RowNode) => {
      node.data = { ...node.data, hover: false };
      itemsToUpdate.push(node.data);
    });
    this.inventoryTrackGrid.api.applyTransaction({ update: itemsToUpdate });
  }

  /**
   * Set To Track Grid
   *
   * @summary Sets the to track grid reference
   * @param $param: AgGridAngular
   * @return void
   */
  setToTrackGrid($param: AgGridAngular): void {
    this.toTrackGrid = $param;
    this.toTrackGrid.context = NSTrackMoveConstants.trackAction;
    this.toTrackGrid.api.addEventListener('rowDragEnd', this.toTrackRowDragEnd.bind(this));
    this.toTrackGrid.api.addEventListener('rowDataChanged', this.setToTrackEquipments.bind(this));
    this.addGridDropZone();
  }

  /**
   * Inventory TracK Row Drag End
   *
   * @summary Validates request and emits move drop event
   * @param drop: ITrackDrop
   * @return void
   */
  inventoryTrackRowDragEnd(drop: ITrackDrop): void {
    this.resetInventoryStyle();
    if (drop.overNode) {
      const itemsToUpdate: Array<ITrackInventory> = [];
      const tracks: Set<string> = new Set<string>();
      const toTrack: ITrack = drop.overNode.data;

      if (drop.nodes.length === 1) {
        itemsToUpdate.push({ ...drop.node.data, toTrack, position: 0 });
        tracks.add(drop.node.data.id);
      } else {
        this.selectedTracks.forEach((node: RowNode) => {
          itemsToUpdate.push({ ...node.data, toTrack, position: 0 });
          tracks.add(node.data.id);
        });
      }
      this.emitMoveTrack(itemsToUpdate, toTrack, drop.node.data.id, tracks);
    }
  }

  /**
   * To TracK Row Drag End
   *
   * @summary Emits drop on to track event
   * @param drop: ITrackDrop
   * @return void
   */
  toTrackRowDragEnd(drop: ITrackDrop): void {
    if (this.selectedToTrack) {
      const itemsToUpdate: Array<ITrackInventory> = [];
      const tracks: Set<string> = new Set<string>();
      let position: number = this.position(drop.api, drop.nodes[0].data.equipmentId);
      if (drop.nodes.length === 1) {
        itemsToUpdate.push({ ...drop.node.data, toTrack: this.selectedToTrack, position });
        tracks.add(drop.node.data.id);
      } else if (this.selectedTracks.size > 0) {
        this.selectedTracks.forEach((node: RowNode) => {
          itemsToUpdate.push({ ...node.data, toTrack: this.selectedToTrack, position });
          tracks.add(node.data.id);
          position++;
        });
      }
      this.resetToTrackGrid();
      this.emitMoveTrack(itemsToUpdate, this.selectedToTrack, drop.node.data.id, tracks);
    } else {
      this.resetToTrackGrid();
      this.showAlert(NSMessages.trackMoveEmptyToTrack);
    }
  }

  /**
   * From TracK Row Drag End
   *
   * @summary Reset's from track cellRendererParams config
   * @return void
   */
  fromTrackRowDragEnd(): void {
    this.fromTrackGrid.api.setRowData([]);
    this.fromTrackGrid.api.setRowData(this.fromTrackData);
    this.resetSelectedTracks();
    this.showAlert(NSMessages.dropNotAllowed);
  }

  /**
   * Add Grid Drop Zone
   *
   * @summary Creates drop zone for To Track and Inventory Track grid
   * @return void
   */
  addGridDropZone(): void {
    if (this.isGridReady) {
      const inventoryGridDropZoneParams = this.inventoryTrackGrid.api.getRowDropZoneParams({});
      this.fromTrackGrid.api.addRowDropZone(inventoryGridDropZoneParams);

      const toTrackGridDropZoneParams = this.toTrackGrid.api.getRowDropZoneParams({});
      this.fromTrackGrid.api.addRowDropZone(toTrackGridDropZoneParams);
    }
  }

  /**
   * Set Inventory Grid Title
   *
   * @summary Sets the inventory track grid title
   * @return void
   */
  setInventoryGridTitle(): void {
    this.toTrackGrid.context = NSTrackMoveConstants.trackAction;
    if (this.inventoryTrackGrid.api.getSelectedNodes().length > 0) {
      const row: RowNode = this.inventoryTrackGrid.api.getSelectedNodes()[0];
      if (row.data.mainTrack) {
        row.setSelected(false);
        this.toTrackGrid.api.setRowData([]);
        this.toTrackSelected.emit(undefined);
        this.showAlert(NSMessages.mainTrackSelected);
      } else {
        this.inventoryGridTitle = row.data.name as ETrackGridTitle;
        this.toTrackSelected.emit(row.data);
      }
    } else {
      this.inventoryGridTitle = ETrackGridTitle.TRACK_NOT_SELECTED;
    }
  }

  /**
   * Update Inventory Grid Selection
   *
   * @summary Sets the row selection if same track is selected
   * @return void
   */
  updateInventoryGridSelection(): void {
    this.inventoryTrackGrid.api.forEachNode((rowNode: RowNode) => {
      if (rowNode.data.id === this.selectedToTrack?.id) {
        if (!rowNode.isSelected()) {
          rowNode.setSelected(true);
        }
      }
    });
  }

  /**
   * From Track Selection
   *
   * @summary Stores grid selection as per selection order
   * @param rows: { node: RowNode }
   * @return void
   */
  fromTrackSelection(rows: { node: RowNode }) {
    if (rows.node.isSelected()) {
      this.selectedTracks.set(rows.node.data.equipmentId, rows.node);
    } else {
      this.selectedTracks.delete(rows.node.data.equipmentId);
    }
  }

  /**
   * Reset Selected Tracks
   *
   * @summary Clears the selected track order
   * @return void
   */
  resetSelectedTracks(): void {
    this.selectedTracks.clear();
  }

  /**
   * Set From Track Equipments
   *
   * @summary Updates row selection based on from track equipments
   * @return void
   */
  setFromTrackEquipments(): void {
    if (this.fromTrackEquipments.length > 0) {
      this.fromTrackGrid.api.forEachNode((rowNode: RowNode) => {
        if (this.fromTrackEquipments.find((equipmentId: string) => rowNode.data.equipmentId === equipmentId)) {
          if (!rowNode.isSelected()) {
            rowNode.setSelected(true);
          }
        }
      });
    }
  }

  /**
   * Set To Track Equipments
   *
   * @summary Updates row selection based on to track equipments
   * @return void
   */
  setToTrackEquipments(): void {
    if (this.toTrackEquipments.length > 0) {
      this.toTrackGrid.api.forEachNode((rowNode: RowNode) => {
        if (this.toTrackEquipments.find((equipmentId: string) => rowNode.data.equipmentId === equipmentId)) {
          if (!rowNode.isSelected()) {
            rowNode.setSelected(true);
          }
        }
      });
    } else {
      this.toTrackGrid.api.deselectAll();
    }
  }

  private validTrackSelected(id: string, itemsToUpdate: Array<ITrackInventory>): boolean {
    return !itemsToUpdate.find((inventory: ITrackInventory) => inventory.id === id);
  }

  private emitMoveTrack(itemsToUpdate: Array<ITrackInventory>, toTrack: ITrack, id: string, tracks: Set<string>): void {
    if (toTrack.mainTrack) {
      this.showAlert(NSMessages.mainTrackSelected);
    } else if (itemsToUpdate.length === 1 && id === toTrack.id) {
      this.showAlert(NSMessages.singleTrackSelected);
    } else if (tracks.size > 1) {
      this.showAlert(NSMessages.multipleTrackSelected);
    } else if (!this.validTrackSelected(toTrack.id, itemsToUpdate) && tracks.size === 1) {
      this.showAlert(NSMessages.singleTrackSelected);
    } else {
      this.resetSelectedTracks();
      this.fromTrackGrid.api.deselectAll();
      this.moveTrack.emit({
        fromTrack: this.selectedFromTrack,
        inventoryToMove: itemsToUpdate
      } as ITrackMovePayload);
    }
  }

  private position(api: GridApi, equipmentId: string): number {
    let position = 1;
    api.forEachNode((node: RowNode) => {
      if (node.data.equipmentId === equipmentId) {
        position = node.rowIndex! + 1;
      }
    });
    return position;
  }

  private resetToTrackGrid(): void {
    this.toTrackGrid.api.setRowData([]);
    this.toTrackGrid.api.setRowData(this.toTrackData);
  }

  private showAlert(text: string): void {
    SweetAlert2.fire({ title: 'Alert', text, icon: 'warning', heightAuto: false, confirmButtonColor: EColors.GOLD });
  }
}
